import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  MessageCircle,
  Mail,
  Building2,
  User as UserIcon,
  Pencil,
  Save,
  XCircle,
  Trash2,
  Archive,
  Sliders,
} from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { phoneToWa } from "../../lib/contact";
import ContactActions from "../ContactActions";
import PhaseChip from "../PhaseChip";
import BlurTextarea from "../BlurTextarea";
import ConfirmModal from "../ConfirmModal";
import CustomerBinModal from "../CustomerBinModal";
import CustomerLogo from "./CustomerLogo";
// Iter 228 — Log a call removed from UI flow; not used in HV daily ops.
import PlaceInput from "../PlaceInput";
import { CUSTOMER_STATUSES } from "../../lib/enums";

const COMPANY_FIELDS = [
  "vat_number",
  "registration_number",
  "industry",
  "website",
  "billing_address",
  "billing_city",
  "billing_postal_code",
  "billing_country",
];

/**
 * Top-of-page customer summary with inline Edit/Save/Cancel + Delete (admin/manager).
 * Read-only display when not editing — same look as before, just edit-aware.
 */
export default function CustomerHeader({ customer, canEdit, onChanged, onDeleted, rightActions }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(customer);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [confirmDel, setConfirmDel] = useState(false);
  const [binOpen, setBinOpen] = useState(false);

  useEffect(() => {
    setForm(customer);
  }, [customer]);

  const isCompany = (form.customer_type || "individual") === "company";

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    setErr("");
    try {
      const payload = { ...form };
      if (!isCompany) COMPANY_FIELDS.forEach((k) => (payload[k] = ""));
      payload.google_rating = payload.google_rating
        ? Number(payload.google_rating)
        : null;
      payload.google_reviews_count = payload.google_reviews_count
        ? Number(payload.google_reviews_count)
        : null;
      // Don't try to override the auto-generated external_ref unless explicitly toggled
      payload.allow_id_override = false;
      await api.put(`/customers/${customer.id}`, payload);
      setEditing(false);
      onChanged?.();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not save"));
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    setForm(customer);
    setEditing(false);
    setErr("");
  };

  const del = async () => {
    setBusy(true);
    try {
      await api.delete(`/customers/${customer.id}`);
      onDeleted?.();
    } finally {
      setBusy(false);
      setConfirmDel(false);
    }
  };

  const wa = (customer.whatsapp || "").replace(/[^0-9]/g, "");
  const tel = (customer.phone || "").replace(/[^0-9+]/g, "");

  const hasCompanyDetails = isCompany && (form.vat_number || form.registration_number || form.industry || form.website || form.billing_address || form.billing_city || form.billing_postal_code || form.billing_country);

  return (
    <div className="hv-card p-4 md:p-5 space-y-3" data-testid="customer-header">
      {/* Top bar: chips + right-side actions */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          {editing ? (
            <select
              value={form.status || "active"}
              onChange={(e) => set("status", e.target.value)}
              className="hv-input !w-auto !py-0.5 !text-xs"
              data-testid="customer-edit-status"
            >
              {CUSTOMER_STATUSES.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          ) : (
            <PhaseChip value={customer.status} />
          )}
          {customer.external_ref && (
            <span
              className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded border border-[#1fb6c1]/40 text-[#1fb6c1] bg-[#1fb6c1]/5"
              data-testid="customer-external-ref-badge"
            >
              {customer.external_ref}
            </span>
          )}
          {editing ? (
            <select
              value={form.customer_type || "individual"}
              onChange={(e) => set("customer_type", e.target.value)}
              className="hv-input !w-auto !py-0.5 !text-xs"
              data-testid="customer-edit-type"
            >
              <option value="individual">Individual</option>
              <option value="company">Company</option>
            </select>
          ) : (
            <span
              className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border inline-flex items-center gap-1 ${
                isCompany
                  ? "border-[#1fb6c1]/40 text-[#1fb6c1] bg-[#1fb6c1]/5"
                  : "border-[#a78bfa]/40 text-[#a78bfa] bg-[#a78bfa]/5"
              }`}
              data-testid="customer-type-badge"
            >
              {isCompany ? (
                <>
                  <Building2 className="w-3 h-3" /> Company
                </>
              ) : (
                <>
                  <UserIcon className="w-3 h-3" /> Individual
                </>
              )}
            </span>
          )}
        </div>

        {/* Right actions — always full-width so buttons wrap inside
            the header block instead of overflowing the viewport. */}
        <div className="flex flex-wrap gap-2 items-stretch sm:items-start sm:justify-end w-full min-w-0">
          {!editing && (tel || wa) && (
            <ContactActions
              phone={tel || wa}
              email={customer?.email}
              name={customer?.name}
              org={customer?.name}
              showVCard
              compact={false}
              mobileOnlyExtras
              testIdPrefix="customer"
            />
          )}
          {!editing && rightActions}
          {/* Iter 228 — "Log call" removed; HV doesn't use the call-log feature. */}
          {canEdit && !editing && (
            <button
              type="button"
              onClick={() => setBinOpen(true)}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="customer-bin-btn"
              title="Recently deleted job cards / tickets / leads"
            >
              <Archive className="w-3.5 h-3.5" /> Bin
            </button>
          )}
          {canEdit && !editing && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="hv-btn hv-btn-ghost"
              data-testid="customer-edit-btn"
            >
              <Pencil className="w-4 h-4" /> Edit
            </button>
          )}
          {canEdit && !editing && (
            <Link
              to={`/customers/${customer.id}/edit`}
              className="hv-btn hv-btn-ghost"
              data-testid="customer-edit-all-btn"
              title="Full profile · all accounting / financial / bank fields"
            >
              <Sliders className="w-4 h-4" /> All fields
            </Link>
          )}
          {canEdit && !editing && (
            <button
              type="button"
              onClick={() => setConfirmDel(true)}
              className="hv-btn hv-btn-ghost text-rose-300"
              data-testid="customer-delete-btn"
              title="Delete customer"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          {editing && (
            <>
              <button type="button" onClick={cancel} disabled={busy} className="hv-btn hv-btn-ghost" data-testid="customer-edit-cancel">
                <XCircle className="w-4 h-4" /> Cancel
              </button>
              <button type="button" onClick={save} disabled={busy} className="hv-btn hv-btn-primary" data-testid="customer-edit-save">
                <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Identity block: name + comms full width */}
      <div className="min-w-0">
        {editing ? (
          <input
            value={form.name || ""}
            onChange={(e) => set("name", e.target.value)}
            className="hv-input text-2xl md:text-3xl font-bold font-display"
            data-testid="customer-edit-name"
            placeholder="Customer name"
          />
        ) : (
          <div className="flex items-center gap-3">
            <CustomerLogo customer={customer} canEdit={canEdit} onChanged={onChanged} />
            <h1
              className="font-display text-2xl md:text-3xl font-bold tracking-tight"
              data-testid="customer-name"
            >
              {customer.name}
            </h1>
          </div>
        )}

        {editing ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-2 mt-3">
            <Field label="Email" value={form.email} onChange={(v) => set("email", v)} editing={editing} testid="customer-edit-email" displayClass="text-[#a0aabf]" wide />
            <Field label="Phone" value={form.phone} onChange={(v) => set("phone", v)} editing={editing} testid="customer-edit-phone" />
            <Field label="WhatsApp" value={form.whatsapp} onChange={(v) => set("whatsapp", v)} editing={editing} testid="customer-edit-whatsapp" />
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mt-2 text-sm">
            {customer.email && (
              <a href={`mailto:${customer.email}`} className="inline-flex items-center gap-1.5 text-[#a0aabf] hover:text-[#1fb6c1] break-all" data-testid="customer-email-display">
                <Mail className="w-3.5 h-3.5 text-[#647087] shrink-0" /> {customer.email}
              </a>
            )}
            {customer.phone && (
              <a href={`tel:${customer.phone}`} className="inline-flex items-center gap-1.5 hover:text-[#1fb6c1] break-all" data-testid="customer-phone-display">
                <Phone className="w-3.5 h-3.5 text-[#647087] shrink-0" /> {customer.phone}
              </a>
            )}
            {customer.whatsapp && customer.whatsapp !== customer.phone && (
              <a href={`https://wa.me/${phoneToWa(customer.whatsapp)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-emerald-300 break-all" data-testid="customer-whatsapp-display">
                <MessageCircle className="w-3.5 h-3.5 text-[#647087] shrink-0" /> {customer.whatsapp}
              </a>
            )}
          </div>
        )}
      </div>

      {/* Physical address with verified-tier picker — always visible (read-only when not editing) */}
      {(editing || form.physical_address) && (
        <div
          className="rounded-md border border-white/5 bg-white/[0.02] p-3"
          data-testid="customer-physical-address-block"
        >
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-2">
            Physical address
          </div>
          {editing ? (
            <PlaceInput
              value={form.physical_address ? { address: form.physical_address, lat: form.lat, lng: form.lng, place_id: form.place_id, source: form.address_source || "legacy" } : null}
              onChange={(v) => {
                set("physical_address", v?.address || "");
                set("lat", v?.lat ?? null);
                set("lng", v?.lng ?? null);
                set("place_id", v?.place_id ?? null);
                set("address_source", v?.source ?? null);
              }}
              testId="customer-edit-physical-address"
            />
          ) : (
            <div className="text-sm break-words" data-testid="customer-physical-address-display">
              {form.physical_address}
            </div>
          )}
        </div>
      )}

      {/* Company details — horizontal strip underneath, full width */}
      {(hasCompanyDetails || editing) && isCompany && (
        <div
          className="rounded-md border border-white/5 bg-white/[0.02] p-3"
          data-testid="customer-company-details"
        >
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-2 flex items-center gap-1">
            <Building2 className="w-3 h-3" /> Company details
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-xs">
            <Field label="VAT / Tax #" value={form.vat_number} onChange={(v) => set("vat_number", v)} editing={editing} mono testid="customer-edit-vat" />
            <Field label="Registration #" value={form.registration_number} onChange={(v) => set("registration_number", v)} editing={editing} mono testid="customer-edit-reg" />
            <Field label="Industry" value={form.industry} onChange={(v) => set("industry", v)} editing={editing} testid="customer-edit-industry" />
            <Field label="Website" value={form.website} onChange={(v) => set("website", v)} editing={editing} testid="customer-edit-website" />
            <Field label="Billing address" value={form.billing_address} onChange={(v) => set("billing_address", v)} editing={editing} testid="customer-edit-billing-address" wide />
            <Field label="City" value={form.billing_city} onChange={(v) => set("billing_city", v)} editing={editing} testid="customer-edit-billing-city" />
            <Field label="Postal code" value={form.billing_postal_code} onChange={(v) => set("billing_postal_code", v)} editing={editing} testid="customer-edit-billing-postal" mono />
            <Field label="Country" value={form.billing_country} onChange={(v) => set("billing_country", v)} editing={editing} testid="customer-edit-billing-country" />
          </div>
        </div>
      )}

      {/* Notes / Special / Internal — full-width below, only render what has content (or all if editing) */}
      {(editing || form.notes || form.special_requests || form.internal_notes) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <PanelEdit label="Notes" value={form.notes} onChange={(v) => set("notes", v)} editing={editing} testid="customer-edit-notes" accent="slate" />
          <PanelEdit label="Special requests" value={form.special_requests} onChange={(v) => set("special_requests", v)} editing={editing} testid="customer-edit-special-requests" accent="amber" />
          <PanelEdit label="Internal notes" value={form.internal_notes} onChange={(v) => set("internal_notes", v)} editing={editing} testid="customer-edit-internal-notes" accent="rose" />
        </div>
      )}

      {err && <div className="text-xs text-rose-300">{err}</div>}

      <ConfirmModal
        open={confirmDel}
        onClose={() => setConfirmDel(false)}
        onConfirm={del}
        title="Delete customer"
        message={
          <>
            Permanently delete <b>{customer.name}</b>? This will also unlink all
            associated sites, contacts, jobs, tickets and calls.
          </>
        }
        confirmLabel="Delete customer"
        testId="customer-delete-confirm"
        busy={busy}
      />

      <CustomerBinModal
        customerId={customer.id}
        open={binOpen}
        onClose={() => setBinOpen(false)}
        onChanged={onChanged}
      />
    </div>
  );
}

function Field({ label, value, onChange, editing, testid, mono, displayClass, wide }) {
  return (
    <div className={`min-w-0 ${wide ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">{label}</div>
      {editing ? (
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className={`hv-input mt-1 ${mono ? "font-mono" : ""}`}
          data-testid={testid}
        />
      ) : (
        <div
          className={`text-sm mt-1 break-all ${mono ? "font-mono" : ""} ${
            displayClass || "text-white"
          } ${value ? "" : "text-[#647087] italic"}`}
        >
          {value || "—"}
        </div>
      )}
    </div>
  );
}

function PanelEdit({ label, value, onChange, editing, testid, accent }) {
  const accents = {
    slate: { border: "border-white/10", bg: "bg-white/[0.02]", title: "text-[#a0aabf]", text: "text-[#dde3ec]" },
    amber: { border: "border-amber-500/30", bg: "bg-amber-500/5", title: "text-amber-300", text: "text-amber-100/90" },
    rose: { border: "border-rose-500/30", bg: "bg-rose-500/5", title: "text-rose-300", text: "text-rose-100/90" },
  };
  const a = accents[accent] || accents.slate;
  if (!editing && !value) return null;
  return (
    <div className={`px-3 py-2 rounded border ${a.border} ${a.bg}`}>
      <div className={`text-[10px] uppercase tracking-[0.2em] ${a.title} font-bold mb-1`}>
        {label}
      </div>
      {editing ? (
        <BlurTextarea
          value={value}
          onSave={onChange}
          testid={testid}
          className="hv-input mt-1 min-h-[60px] !bg-transparent"
          placeholder={`Add ${label.toLowerCase()}…`}
        />
      ) : (
        <div className={`text-sm whitespace-pre-wrap leading-relaxed ${a.text}`}>{value}</div>
      )}
    </div>
  );
}
